import * as React from "react";
import { Link } from "react-router-dom";
import { UseQueryResult } from "@tanstack/react-query";

import { getApiErrorMessage } from "api/apiError";
import { BillingOverview } from "api/billing";
import { Button, Card, CardHeader, ErrorState, Skeleton } from "Components/ui";
import { cn } from "utils/cn";
import { formatINR, pluralize } from "utils/format";

const Figure: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  alert?: boolean;
}> = ({ label, value, hint, alert = false }) => (
  <div className="min-w-0">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd
      className={cn(
        "mt-0.5 truncate text-lg font-semibold tracking-tight",
        alert && "text-danger-text"
      )}
    >
      {value}
    </dd>
    {hint && <dd className="text-xs text-muted-foreground">{hint}</dd>}
  </div>
);

/** What clients owe, from the billing overview (same numbers as the Billing page). */
const BillingCard: React.FC<{ billing: UseQueryResult<BillingOverview> }> = ({ billing }) => {
  const kpis = billing.data?.kpis;
  let body: React.ReactNode;
  if (billing.isLoading) {
    body = (
      <div className="grid grid-cols-2 gap-4">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    );
  } else if (billing.isError || !kpis) {
    body = (
      <ErrorState
        compact
        title="Billing didn't load"
        description={getApiErrorMessage(billing.error)}
      />
    );
  } else {
    body = (
      <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
        <Figure label="Outstanding" value={formatINR(kpis.outstanding)} />
        <Figure
          label="Overdue"
          value={formatINR(kpis.overdueAmount)}
          hint={
            kpis.overdueCount > 0
              ? `${pluralize(kpis.overdueCount, "client")} past due`
              : "Nobody is past due"
          }
          alert={kpis.overdueAmount > 0}
        />
        <Figure
          label="Read-only now"
          value={kpis.readOnlyCount}
          hint="TeleOpd blocks staff changes"
          alert={kpis.readOnlyCount > 0}
        />
        <Figure label="Collected this month" value={formatINR(kpis.collectedThisMonth)} />
      </dl>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Billing"
        actions={
          <Button asChild variant="secondary" size="sm">
            <Link to="/billing">Open billing</Link>
          </Button>
        }
      />
      <div className="px-5 pb-5 pt-4">{body}</div>
    </Card>
  );
};

export default BillingCard;
